import { useState, useEffect } from "react";
import { fetchUserStatement } from "../../../apiCalls/urlFunction";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Chip,
  Skeleton,
  Container,
  Button,
  Paper,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { dateChanger } from "../../../shared/extra";

import { motion } from 'framer-motion';
import {
  ReceiptLong,
  AccountBalance,
  History,
  TrendingUp,
  AttachMoney,
  CalendarToday,


} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag } from "lucide-react";
import { GridArrowUpwardIcon } from "@mui/x-data-grid";
import ReceiptIcon from "@mui/icons-material/Receipt";

const EmptyStatementScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const floatAnimation = {
    y: [0, -8, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  const pulseGlow = {
    scale: [1, 1.08, 1],
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 2.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  // Quick action buttons
  const quickActions = [
    {
      icon: <History sx={{ fontSize: 20 }} />,
      label: 'Recent Orders',
      color: '#667eea',
      onClick: () => navigate('/profile/order'),
    },
    {
      icon: <AccountBalance sx={{ fontSize: 20 }} />,
      label: 'Gift Cards',
      color: '#e9b367',
      onClick: () => navigate('/card-list'),
    },
    {
      icon: <AttachMoney sx={{ fontSize: 20 }} />,
      label: 'Transactions',
      color: '#f093fb',
      onClick: () => navigate('/profile/order'),
    },
  ];

  // Activity timeline (empty state suggestions)
  const suggestions = [
    {
      icon: <ReceiptLong sx={{ fontSize: 16 }} />,
      label: 'First Purchase',
      description: 'Make your first gift card purchase',
      time: 'Start now',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 16 }} />,
      label: 'View Activity',
      description: 'Track your gift card usage',
      time: 'Explore',
    },
    {
      icon: <CalendarToday sx={{ fontSize: 16 }} />,
      label: 'Check History',
      description: 'Review your past transactions',
      time: 'View',
    },
  ];

  return (
    <Container
      maxWidth="xs"
      disableGutters
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 3,
        background: `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.5)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      }}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{
          width: '100%',
          maxWidth: '420px',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative Elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#667eea', 0.06)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#e9b367', 0.06)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          {/* Statement Icon */}
          <motion.div
            variants={itemVariants}
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 28,
            }}
          >
            <Box sx={{ position: 'relative' }}>
              {/* Glow Effect */}
              <motion.div
                animate={pulseGlow}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${alpha('#667eea', 0.12)} 0%, transparent 70%)`,
                }}
              />

              {/* Main Icon */}
              <motion.div
                animate={floatAnimation}
                style={{
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${alpha('#667eea', 0.08)} 0%, ${alpha('#e9b367', 0.08)} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${alpha('#667eea', 0.12)}`,
                    position: 'relative',
                  }}
                >
                  <ReceiptLong
                    sx={{
                      fontSize: 48,
                      color: '#e9b367',
                    }}
                  />

                  {/* Decorative Accent */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, #e9b367 0%, #d4a050 100%)`,
                      opacity: 0.4,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      left: -4,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                      opacity: 0.3,
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          </motion.div>

          {/* Title */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontFamily: 'Onest, Be Vietnam Pro, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.6rem', sm: '1.8rem' },
                mb: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 60%, ${theme.palette.primary.main} 100%)`,
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              No Statement Yet
            </Typography>
          </motion.div>

          {/* Description */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="body1"
              align="center"
              sx={{
                fontFamily: 'Be Vietnam Pro, sans-serif',
                fontSize: { xs: '0.95rem', sm: '1rem' },
                color: alpha(theme.palette.text.secondary, 0.8),
                mb: 3,
                lineHeight: 1.7,
                maxWidth: '320px',
                mx: 'auto',
              }}
            >
              Your transaction history will appear here once you start purchasing and using gift vouchers.
            </Typography>
          </motion.div>

          {/* Main CTA */}
          <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/card-list')}
              sx={{
                py: { xs: 1.5, sm: 1.75 },
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 50%, ${theme.palette.primary.main} 100%)`,
                backgroundSize: '200% 200%',
                animation: 'gradientMove 3s ease infinite',
                boxShadow: '0 8px 30px rgba(233, 179, 103, 0.25)',
                fontFamily: 'Onest, Be Vietnam Pro, sans-serif',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                textTransform: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: '0 12px 40px rgba(233, 179, 103, 0.35)',
                  transform: 'translateY(-2px)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
                  transition: 'all 0.8s ease',
                },
                '&:hover::after': {
                  left: '100%',
                },
              }}
              startIcon={<ShoppingBag sx={{ fontSize: 22 }} />}
            >
              Explore Gift Vouchers
            </Button>
          </motion.div>
        </Paper>
      </motion.div>

      {/* CSS Animation */}
      <style>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </Container>
  );
};
const EmptyData = () => {
  return (
    <Box
      sx={{
        minHeight: 350,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        px: 2,
      }}
    >


      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#1F2937",
          mb: 1,
        }}
      >
        No Transactions  Data Found
      </Typography>

    </Box>
  )
}

const StatementSkeleton = () => {
  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        bgcolor: "#ffffff",
        borderRadius: 3,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
        display: { xs: "block", sm: "block" },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Skeleton
              variant="circular"
              animation="wave"
              width={44}
              height={44}
              sx={{ flexShrink: 0, bgcolor: "#f5f5f5" }}
            />

            {/* Texts and Tag Skeletons */}
            <Stack spacing={0.5}>
              {/* Transaction Type Placeholder */}
              <Skeleton
                variant="text"
                animation="wave"
                width={130}
                height={16}
                sx={{ transform: "none", borderRadius: 0.5 }}
              />
              {/* Order ID/Remark Placeholder */}
              <Skeleton
                variant="text"
                animation="wave"
                width={80}
                height={16}
                sx={{ transform: "none", borderRadius: 0.5 }}
              />
              {/* ORDER Chip Placeholder */}
              <Skeleton
                variant="rectangular"
                animation="wave"
                width={55}
                height={20}
                sx={{ borderRadius: 1, mt: 0.5 }}
              />
            </Stack>
          </Stack>

          {/* Amount and Type Badge Skeletons */}
          <Stack spacing={0.5} alignItems="flex-end">
            {/* Price Placeholder */}
            <Skeleton
              variant="text"
              animation="wave"
              width={85}
              height={18}
              sx={{ transform: "none", borderRadius: 0.5 }}
            />
            {/* DEBIT Chip Placeholder */}
            <Skeleton
              variant="rectangular"
              animation="wave"
              width={50}
              height={20}
              sx={{ borderRadius: 1, mt: 0.5 }}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            bgcolor: "#f1fbff",
            borderRadius: 2,
            p: 1.5,
            mt: 2,
            mb: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-around"
            alignItems="center"
          >
            <Stack alignItems="center" spacing={0.5}>
              <Skeleton
                variant="text"
                animation="wave"
                width={80}
                height={12}
                sx={{ transform: "none" }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                width={90}
                height={16}
                sx={{ transform: "none" }}
              />
            </Stack>

            <Skeleton
              variant="circular"
              animation="wave"
              width={18}
              height={18}
            />

            <Stack alignItems="center" spacing={0.5}>
              <Skeleton
                variant="text"
                animation="wave"
                width={85}
                height={12}
                sx={{ transform: "none" }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                width={90}
                height={16}
                sx={{ transform: "none" }}
              />
            </Stack>
          </Stack>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Skeleton
              variant="circular"
              animation="wave"
              width={16}
              height={16}
            />
            <Skeleton
              variant="text"
              animation="wave"
              width={120}
              height={14}
              sx={{ transform: "none" }}
            />
          </Stack>

          {/* Timestamp Identifier */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Skeleton
              variant="circular"
              animation="wave"
              width={16}
              height={16}
            />
            <Skeleton
              variant="text"
              animation="wave"
              width={110}
              height={14}
              sx={{ transform: "none" }}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function MobileSatement() {
  const [statementData, setStatementData] = useState();
  const [Loading, setLoading] = useState();
  const [filterType, setFilterType] = useState("ALL");
  const fontFamily = "Be Vietnam Pro";

  const fetchUserStatementData = async () => {
    try {
      setLoading(true);
      const res = await fetchUserStatement();
      if (res?.success) {
        const senitizeRespose = res?.data?.edges?.map(
          (item, idx) => item?.node,
        );
        setStatementData(senitizeRespose);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    return;
  };

  useEffect(() => {
    fetchUserStatementData();
  }, []);

  const filteredData =
    filterType === "ALL"
      ? statementData
      : statementData?.filter(
        (item) => item?.transactionType?.toUpperCase() === filterType,
      );



  if (Loading) return Array?.from({ length: 5 })?.map((_, index) => <StatementSkeleton key={index} />);

  if (!Loading && statementData?.length === 0) return <EmptyStatementScreen />;
  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          width: "100%",
          mb: 2,
        }}
      >
        <ReceiptIcon
          sx={{
            color: "primary.main",
            fontSize: 25,

            cursor: "pointer",
            "&:hover": {
              color: "secondary.main",
            },
          }}
        />
        <Typography
          sx={{
            fontFamily: fontFamily,
            fontSize: "18px",
            color: "#000",
          }}
        >
          My Statement
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
        >
          {["ALL", "DEBIT", "CREDIT"].map((type) => {
            const isSelected = filterType === type;

            return (

              <Chip
                key={type}
                label={type}
                onClick={() => setFilterType(type)}
                size="small"
                sx={{
                  cursor: "pointer",
                  fontWeight: 600,
                  fontFamily: fontFamily,
                  px: 1,
                  fontSize: "9px",
                  height: "31px",
                  textTransform: "capitalize",
                  bgcolor: isSelected ? "#F3F0FF" : "#FFFFFF",
                  color: isSelected ? "#4F35CD" : "#000",
                  border: isSelected
                    ? "1px solid #4F35CD"
                    : "1px solid #E2E8F0",
                  "&:hover": {
                    bgcolor: isSelected ? "#EAE5FF" : "#F8FAFC",
                    border: isSelected
                      ? "1px solid #4F35CD"
                      : "1px solid #CBD5E1",
                  },
                }}
              />
            );
          })}
        </Stack>

      </Box>

      {filteredData?.length === 0 ? (
        <EmptyData />
      ) : (

        filteredData?.map((item, index) => (
          <Card
            key={item?.id || index}
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: 3,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
              fontFamily: fontFamily,
              display: { xs: "block", sm: "block" },
            }}
          >
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  width: "100%",
                  gap: 2,
                }}
              >
                {/* Left Side */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {/* Transaction Icon */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      bgcolor:
                        item?.transactionType?.toUpperCase() === "DEBIT"
                          ? "#FCE8E6"
                          : "#E8F5E9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item?.transactionType?.toUpperCase() === "DEBIT" ? (
                      <ArrowDownwardIcon
                        sx={{
                          color: "#C5221F",
                          fontSize: 22,
                        }}
                      />
                    ) : (
                      <GridArrowUpwardIcon
                        sx={{
                          color: "#2E7D32",
                          fontSize: 22,
                        }}
                      />
                    )}
                  </Box>

                  {/* Balance + Remarks */}
                  <Stack
                    spacing={0.5}
                    sx={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {/* Balance */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#000",
                        lineHeight: 1.2,
                        fontFamily: fontFamily,
                        whiteSpace: "nowrap",
                      }}
                    >
                      AED {item?.closingBalance ?? 0}
                    </Typography>

                    {/* Remarks */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: "#6B7280",
                        fontSize: "12px",
                        lineHeight: 1.4,
                        fontFamily: fontFamily,
                        overflow: "hidden",

                        whiteSpace: "wrap",
                      }}
                    >
                      {item?.remarks || "No remarks"}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Right Side - Transaction Type */}
                <Chip
                  label={item?.transactionType || "N/A"}
                  size="small"
                  sx={{
                    bgcolor:
                      item?.transactionType?.toUpperCase() === "DEBIT"
                        ? "#FCE8E6"
                        : "#E8F5E9",
                    color:
                      item?.transactionType?.toUpperCase() === "DEBIT"
                        ? "#C5221F"
                        : "#2E7D32",
                    fontWeight: 600,
                    fontSize: "10px",
                    height: 20,
                    borderRadius: 1,
                    fontFamily: fontFamily,
                    flexShrink: 0,

                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              </Stack>


              <Box
                sx={{
                  bgcolor: "#f1fbff",
                  borderRadius: 2,
                  p: 1.5,
                  mt: 2,
                  mb: 2,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-around"
                  alignItems="center"
                >
                  {/* Opening Balance */}
                  <Stack alignItems="center" spacing={0.5}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        fontSize: "13px",
                        fontFamily: fontFamily,
                      }}
                    >
                      Opening Balance
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#1A1C1E",
                        fontFamily: fontFamily,
                      }}
                    >
                      AED {item?.openingBalance || "N/A"}
                    </Typography>
                  </Stack>

                  {/* Flow Arrow */}
                  <ArrowForwardIcon sx={{ color: "#C4C6CF", fontSize: 18 }} />

                  {/* Closing Balance */}
                  <Stack alignItems="center" spacing={0.5}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        fontSize: "13px",
                        fontFamily: fontFamily,
                      }}
                    >
                      Closing Balance
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#000",
                        fontFamily: fontFamily,

                      }}
                    >
                      AED {item?.closingBalance ?? 0}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Bottom Section: Footer Specs */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                {/* Transaction Info */}
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ReceiptLongIcon sx={{ color: "#primary.main", fontSize: 16 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "primary.main",
                      fontWeight: 500,
                      fontFamily: fontFamily,
                      fontSize: "11px",
                    }}
                  >
                    Transaction ID:{" "}
                    <Box
                      component="span"
                      sx={{ color: "#000", fontFamily: fontFamily, fontSize: "10px" }}
                    >
                      {item?.transactionId || "N/A"}
                    </Box>
                  </Typography>
                </Stack>

                {/* Timestamp */}
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CalendarMonthIcon
                    sx={{ color: "#74777F", fontSize: 16 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#000",
                      fontSize: "11px",
                      fontWeight: 500,
                      fontFamily: fontFamily,
                    }}
                  >
                    {dateChanger(item?.createdAt) || "N/A"}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )))}

    </Stack>
  );
}
